import * as React from 'react'
import DOMPurify from 'dompurify'
import { connect } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { ArrowRight, ChevronLeft, ExternalLink, Plus, Trash2, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import PageLoader from '../../components/Page/PageLoader'
import SwatchPicker from '../../components/Swatch/SwatchPicker'
import Notification from '../../utilities/Notification'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'
import DroppableHelper from '../../helpers/DroppableHelper'
import { showConfirmModal, showWidgetTourStepModal } from '../../store/modals/actions'
import { WidgetToursController, WidgetTourStepsController } from '../../controllers'
import { WidgetTour as WidgetTourType, VideoPointerWidgetTourStep, Widget, WidgetTourStep, WidgetTourStepType } from '../../types'

const DragHandleIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'><path d='M13 9C12.17 9 11.5 9.67 11.5 10.5C11.5 11.33 12.17 12 13 12C13.83 12 14.5 11.33 14.5 10.5C14.5 9.67 13.83 9 13 9ZM3 9C2.17 9 1.5 9.67 1.5 10.5C1.5 11.33 2.17 12 3 12C3.83 12 4.5 11.33 4.5 10.5C4.5 9.67 3.83 9 3 9ZM8 9C7.17 9 6.5 9.67 6.5 10.5C6.5 11.33 7.17 12 8 12C8.83 12 9.5 11.33 9.5 10.5C9.5 9.67 8.83 9 8 9ZM13 7C13.83 7 14.5 6.33 14.5 5.5C14.5 4.67 13.83 4 13 4C12.17 4 11.5 4.67 11.5 5.5C11.5 6.33 12.17 7 13 7ZM3 4C2.17 4 1.5 4.67 1.5 5.5C1.5 6.33 2.17 7 3 7C3.83 7 4.5 6.33 4.5 5.5C4.5 4.67 3.83 4 3 4ZM8 4C7.17 4 6.5 4.67 6.5 5.5C6.5 6.33 7.17 7 8 7C8.83 7 9.5 6.33 9.5 5.5C9.5 4.67 8.83 4 8 4Z' /></svg>
)

interface IStepCardProps {
  step: WidgetTourStep
  index: number
  senderName: string
  onClick: (step: WidgetTourStep) => void
  onRemove: (step: WidgetTourStep) => void
}

const StepCard = ({ step, index, senderName, onClick, onRemove }: IStepCardProps) => {
  const { t } = useTranslation()
  const mediaUrl = (step as VideoPointerWidgetTourStep).media_url

  return (
    <Draggable draggableId={step.id} index={index}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps} className='relative max-h-[148px] shrink-0 pr-12'>
          <div
            className='group relative flex h-full min-w-[214px] max-w-[214px] cursor-pointer flex-col overflow-hidden rounded-xl bg-card shadow-sm ring-1 ring-foreground/10 hover:ring-foreground/20'
            onClick={() => onClick(step)}
          >
            <div className='flex items-center justify-between gap-2 p-3'>
              <div className='flex min-w-0 items-center gap-2'>
                <span className='flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-muted-foreground'>
                  <UserRound className='size-3.5' />
                </span>
                <span className='truncate text-xs font-medium text-foreground'>{senderName}</span>
              </div>

              <div className='flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100'>
                <span
                  {...provided.dragHandleProps}
                  aria-label={t('WidgetTour::Reorder step')}
                  onClick={(e) => e.stopPropagation()}
                  className='flex size-7 cursor-grab items-center justify-center rounded-md bg-muted text-muted-foreground hover:bg-muted/70 active:cursor-grabbing'
                >
                  <DragHandleIcon />
                </span>
                <button
                  type='button'
                  aria-label={t('WidgetTour::Remove step')}
                  onClick={(e) => { e.stopPropagation(); onRemove(step) }}
                  className='flex size-7 cursor-pointer items-center justify-center rounded-md bg-muted text-destructive hover:bg-destructive/10'
                >
                  <Trash2 className='size-3.5' />
                </button>
              </div>
            </div>

            {step.type === WidgetTourStepType.VIDEO_POINTER && mediaUrl && (
              <video src={mediaUrl} className='aspect-video w-full bg-black object-cover' muted />
            )}

            <div className='flex flex-col gap-1 px-3 pb-3'>
              <p className='text-sm font-semibold text-foreground'>{step.title}</p>
              {step.description && (
                <div
                  className='line-clamp-3 text-sm text-muted-foreground [&_a]:underline [&_img]:max-w-full [&_iframe]:w-full [&_iframe]:rounded-md [&_p]:m-0'
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(step.description) }}
                />
              )}
            </div>
          </div>

          <div className='absolute top-1/2 right-0 z-10 flex size-6 -translate-y-1/2 shrink-0 items-center justify-center rounded-md bg-foreground text-background'>
            <ArrowRight className='size-3.5' />
          </div>
        </div>
      )}
    </Draggable>
  )
}

interface IDispatchToProps {
  showWidgetTourStepModal: typeof showWidgetTourStepModal
  showConfirmModal: typeof showConfirmModal
}

type IProps = IDispatchToProps

interface IState {
  tour: WidgetTourType | null
  widget: Widget | null
  didInitialLoad: boolean
}

const WidgetTour = (props: IProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { id: widgetId, tourId } = useParams<{ id: string, tourId: string }>()

  const [state, setState] = React.useState<IState>({
    tour: null,
    widget: null,
    didInitialLoad: false,
  })
  const { tour, widget, didInitialLoad } = state

  const fetchTour = async () => {
    try {
      const response = await WidgetToursController.getTour(widgetId, tourId)
      setState((current) => ({ ...current, tour: response.tour, widget: response.widget, didInitialLoad: true }))
    } catch (ex) {
      console.error(ex)
      setState((current) => ({ ...current, didInitialLoad: true }))
    }
  }

  React.useEffect(() => {
    fetchTour()
  }, [tourId])

  // The builder tab is a separate real browser tab on the customer's own
  // site (not an iframe - most sites block being framed anyway), so the only
  // way it can tell us a step was saved is postMessage.
  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'tour_builder_saved') return
      fetchTour()
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [tourId])

  if (!didInitialLoad) return <PageLoader />
  if (!tour) return null

  const steps = tour.steps || []
  const senderName = t('WidgetTour::{{author}} from {{widget}}', {
    author: tour.author?.name || '',
    widget: widget?.name || '',
  })

  const updateTour = async (update: Partial<WidgetTourType>) => {
    const updated = { ...tour, ...update }
    setState((current) => ({ ...current, tour: updated }))

    try {
      const response = await WidgetToursController.update(widgetId, updated)

      if (response.errors) {
        Notification.notifyError(t('WidgetTour::Failed to save tour'))
      } else {
        setState((current) => ({ ...current, tour: { ...current.tour, ...response } }))
      }
    } catch (ex) {
      console.error(ex)
      Notification.notifyError(t('WidgetTour::Failed to save tour'))
    }
  }

  const onNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const name = e.currentTarget.value
    if (!name || name === tour.name) return
    updateTour({ name })
  }

  const onEnabledChange = (enabled: boolean) => {
    updateTour({ enabled })
  }

  const onUseCustomColorChange = (checked: boolean) => {
    updateTour({ color: checked ? (tour.color || '#0071b2') : null })
  }

  const onColorChange = (color: string) => {
    updateTour({ color })
  }

  const onUseCustomBackgroundChange = (checked: boolean) => {
    updateTour({ background_color: checked ? (tour.background_color || '#f5f5f5') : null })
  }

  const onBackgroundColorChange = (background_color: string) => {
    updateTour({ background_color })
  }

  const onAddStepClick = () => {
    const position = steps.length > 0 ? Math.max(...steps.map(s => s.position || 0)) + 1 : 1

    props.showWidgetTourStepModal({
      widgetId,
      tourId,
      step: { type: WidgetTourStepType.POST, position, page_path: '/' },
      onSubmit: (step) => {
        setState((current) => ({
          ...current,
          tour: { ...current.tour, steps: [...(current.tour.steps || []), step] },
        }))
      },
    })
  }

  const onStepClick = (step: WidgetTourStep) => {
    props.showWidgetTourStepModal({
      widgetId,
      tourId,
      step,
      onSubmit: (updated) => {
        setState((current) => ({
          ...current,
          tour: { ...current.tour, steps: (current.tour.steps || []).map(s => s.id === updated.id ? updated : s) },
        }))
      },
    })
  }

  const onRemoveStepClick = (step: WidgetTourStep) => {
    props.showConfirmModal({
      title: t('WidgetTour::Remove step'),
      description: t('WidgetTour::You are about to remove this step. Are you sure?'),
      action: { label: t('WidgetTour::Remove'), isDestructive: true },
      onConfirm: async () => {
        setState((current) => ({
          ...current,
          tour: { ...current.tour, steps: (current.tour.steps || []).filter(s => s.id !== step.id) },
        }))

        try {
          const response = await WidgetTourStepsController.delete(widgetId, tourId, step.id)

          if (response.errors) {
            Notification.notifyError(t('WidgetTour::Failed to remove step'))
            fetchTour()
          }
        } catch (ex) {
          console.error(ex)
          Notification.notifyError(t('WidgetTour::Failed to remove step'))
          fetchTour()
        }
      },
    })
  }

  const onEditOnWebsiteClick = () => {
    // Must open synchronously in the click handler - opening after the
    // createBuilderToken() promise resolves would get blocked as a popup.
    const builderTab = window.open('about:blank', '_blank')

    WidgetToursController.createBuilderToken(widgetId, tourId).then(({ token, start_url }) => {
      if (!builderTab) {
        Notification.notifyError(t('WidgetTour::Please allow pop-ups to edit your tour on your website'))
        return
      }

      const startUrl = start_url || window.prompt(t('WidgetTour::Which page of your website should we open?')) || ''
      if (!startUrl) {
        builderTab.close()
        return
      }

      const separator = startUrl.includes('?') ? '&' : '?'
      builderTab.location.href = `${startUrl}${separator}bizzey_tour_builder=${encodeURIComponent(token)}`
    }).catch((ex) => {
      console.error(ex)
      builderTab?.close()
      Notification.notifyError(t('WidgetTour::Failed to start the tour builder'))
    })
  }

  const onBackClick = () => {
    history.push(RouteHelper.process(ERoute.PATH_WIDGET_TOURS, { id: widgetId }))
  }

  const onDragStepEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination || source.index === destination.index) return

    const newPosition = DroppableHelper.getNewPositionBasedOnDropResult(steps, result)
    const movedStep = steps[source.index]
    if (!movedStep || newPosition === undefined) return

    const updatedStep = { ...movedStep, position: newPosition }

    setState((current) => ({
      ...current,
      tour: {
        ...current.tour,
        steps: (current.tour.steps || [])
          .map(s => s.id === updatedStep.id ? updatedStep : s)
          .sort((a, b) => (a.position || 0) - (b.position || 0)),
      },
    }))

    WidgetTourStepsController.update(widgetId, tourId, updatedStep).then((response) => {
      if (response.errors) {
        Notification.notifyError(t('WidgetTour::Failed to reorder steps'))
        fetchTour()
      }
    }).catch((ex) => {
      console.error(ex)
      Notification.notifyError(t('WidgetTour::Failed to reorder steps'))
      fetchTour()
    })
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-4'>
        <Button variant='ghost' size='sm' onClick={onBackClick}>
          <ChevronLeft />
          {t('WidgetTour::Tours')}
        </Button>

        <div className='flex items-center gap-3'>
          <Button variant='outline' size='sm' onClick={onEditOnWebsiteClick}>
            <ExternalLink />
            {t('WidgetTour::Edit on your website')}
          </Button>

          <span className='text-sm text-muted-foreground'>{tour.enabled ? t('WidgetTour::Enabled') : t('WidgetTour::Disabled')}</span>
          <Switch checked={Boolean(tour.enabled)} onCheckedChange={onEnabledChange} />
        </div>
      </div>

      <Input
        defaultValue={tour.name || ''}
        key={tour.id}
        placeholder={t('WidgetTour::Name your tour')}
        onBlur={onNameBlur}
        className='h-auto border-0! bg-transparent! px-0! py-0! text-2xl font-semibold shadow-none focus-visible:ring-0'
      />

      <Card className='overflow-visible py-0'>
        <div className='grid grid-cols-1 md:grid-cols-[280px_1fr]'>
          <div className='flex flex-col gap-6 p-5 md:border-r md:border-border'>
            <CardHeader className='p-0'>
              <CardTitle>{t('WidgetTour::Styles')}</CardTitle>
            </CardHeader>

            <div className='flex flex-col divide-y divide-border'>
              <div className='flex items-start justify-between gap-3 pb-4'>
                <div className='flex flex-col gap-1'>
                  <p className='text-sm font-medium text-foreground'>{t('WidgetTour::Use custom color')}</p>
                  <p className='text-sm text-muted-foreground'>{t('WidgetTour::Use this color for buttons and highlights')}</p>
                  {tour.color && (
                    <div className='mt-1'>
                      <SwatchPicker color={tour.color} onChange={onColorChange} />
                    </div>
                  )}
                </div>
                <Switch checked={Boolean(tour.color)} onCheckedChange={onUseCustomColorChange} />
              </div>

              <div className='flex items-start justify-between gap-3 pt-4'>
                <div className='flex flex-col gap-1'>
                  <p className='text-sm font-medium text-foreground'>{t('WidgetTour::Use custom background color')}</p>
                  <p className='text-sm text-muted-foreground'>{t('WidgetTour::Use this color for background')}</p>
                  {tour.background_color && (
                    <div className='mt-1'>
                      <SwatchPicker color={tour.background_color} onChange={onBackgroundColorChange} />
                    </div>
                  )}
                </div>
                <Switch checked={Boolean(tour.background_color)} onCheckedChange={onUseCustomBackgroundChange} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-4 p-5'>
            <CardHeader className='p-0'>
              <CardTitle>{t('WidgetTour::Tour steps')}</CardTitle>
              <CardDescription>{t('WidgetTour::Guide visitors through your product, one step at a time.')}</CardDescription>
            </CardHeader>

            <CardContent className='p-0'>
              <div className='flex items-stretch gap-8 overflow-x-auto pt-px pb-2 pl-2'>
                <DragDropContext onDragEnd={onDragStepEnd}>
                  <Droppable droppableId='tour-steps' direction='horizontal' type='tour-steps'>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className='flex items-stretch gap-8'>
                        {steps.map((step, index) => (
                          <StepCard
                            key={step.id}
                            step={step}
                            index={index}
                            senderName={senderName}
                            onClick={onStepClick}
                            onRemove={onRemoveStepClick}
                          />
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <button
                  type='button'
                  aria-label={t('WidgetTour::Add steps')}
                  onClick={onAddStepClick}
                  className='flex max-h-[148px] min-h-[150px] w-32 min-w-[214px] max-w-[214px] shrink-0 cursor-pointer items-center justify-center rounded-xl bg-muted transition-colors hover:bg-muted/70'
                >
                  <span className='flex size-11 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-foreground/10'>
                    <Plus className='size-5' />
                  </span>
                </button>
              </div>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  )
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => ({
  showWidgetTourStepModal: (options) => dispatch(showWidgetTourStepModal(options)),
  showConfirmModal: (options) => dispatch(showConfirmModal(options)),
})

export default connect(null, mapDispatchToProps)(WidgetTour)
